/**
 * 主题工具函数
 * 用于管理应用的主题颜色设置
 */

const THEME_STORAGE_KEY = 'app_theme_config';

/**
 * 主题颜色配置接口
 */
export interface ThemeConfig {
  background: string;     // 背景色（app-shell-body的颜色）
  primary: string;        // 前景色（主色，用于个人信息卡片等）
  primaryText: string;    // 字体色（主色区域的文字颜色）
  button: string;         // 按钮色（按钮背景颜色）
  buttonText: string;     // 按钮文字颜色
  name: string;           // 主题名称
}

/**
 * 预设主题配置
 */
export const PRESET_THEMES: ThemeConfig[] = [
  {
    name: '纯白',
    background: '#FFFFFF',
    primary: '#FFFFFF',
    primaryText: '#000000',
    button: '#E5E5F3',
    buttonText: '#1e293b',
  },
  {
    name: '米白',
    background: '#F1E6D8',
    primary: '#FAEDD1',
    primaryText: '#1387C0',
    button: '#FAF3E9',
    buttonText: '#1387C0',
  },
  {
    name: '草木青',
    background: '#D3F2DE',
    primary: '#99C39E',
    primaryText: '#ffffff',
    button: '#506850',
    buttonText: '#ffffff',
  },
];

/**
 * 默认主题
 */
const DEFAULT_THEME: ThemeConfig = PRESET_THEMES[1]; // 纯白色

/**
 * 获取当前主题配置
 */
export const getThemeConfig = (): ThemeConfig => {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored) {
    try {
      const config = JSON.parse(stored);
      // 迁移旧配置：如果配置中没有某些字段，或者与预设主题不匹配，则更新
      const matchingPreset = PRESET_THEMES.find(p => 
        p.primary === config.primary && p.button === config.button
      );
      if (matchingPreset) {
        // 如果找到匹配的预设主题，使用预设主题的完整配置
        return matchingPreset;
      }
      // 如果没有匹配的预设，补充缺失的字段
      if (!config.background) {
        config.background = '#FFFFFF'; // 默认白色背景
      }
      if (!config.primaryText) {
        // 浅色背景（白色等）使用黑色文字，深色背景使用白色文字
        const isLightColor = config.primary === '#FFFFFF' || 
                             config.primary.toLowerCase() === '#ffffff';
        config.primaryText = isLightColor ? '#000000' : '#ffffff';
      }
      // 保存更新后的配置
      if (!config.background || !config.primaryText) {
        localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(config));
      }
      return config;
    } catch {
      return DEFAULT_THEME;
    }
  }
  return DEFAULT_THEME;
};

/**
 * 获取当前主题颜色（兼容旧代码）
 */
export const getThemeColor = (): string => {
  return getThemeConfig().button;
};

/**
 * 设置主题配置
 */
export const setThemeConfig = (config: ThemeConfig): void => {
  localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(config));
  applyThemeConfig(config);
};

/**
 * 应用主题配置到 CSS 变量
 */
export const applyThemeConfig = (config: ThemeConfig): void => {
  document.documentElement.style.setProperty('--theme-background-color', config.background);
  document.documentElement.style.setProperty('--theme-primary-color', config.primary);
  document.documentElement.style.setProperty('--theme-primary-text-color', config.primaryText);
  document.documentElement.style.setProperty('--theme-button-color', config.button);
  document.documentElement.style.setProperty('--theme-button-text-color', config.buttonText);
};

/**
 * 设置主题颜色（兼容旧代码，使用第一个预设主题）
 */
export const setThemeColor = (color: string, textColor?: string): void => {
  const preset = PRESET_THEMES.find(t => t.button === color);
  if (preset) {
    setThemeConfig(preset);
  } else {
    // 如果没有找到预设，创建一个临时配置
    // 根据背景颜色自动判断文字颜色
    const isLightColor = color === '#FFFFFF' || 
                         color.toLowerCase() === '#ffffff';
    const config: ThemeConfig = {
      name: '自定义',
      background: '#FFFFFF', // 默认白色背景
      primary: color,
      primaryText: isLightColor ? '#000000' : '#ffffff',
      button: color,
      buttonText: textColor || (isLightColor ? '#000000' : '#ffffff'),
    };
    setThemeConfig(config);
  }
};

/**
 * 初始化主题（在应用启动时调用）
 */
export const initTheme = (): void => {
  const config = getThemeConfig();
  applyThemeConfig(config);
};

